import { useState, useEffect, useMemo, createContext, useContext } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Menu, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../utils/auth';

import { getDadosLancamentosDash, getDadosDespesas, getDadosOrcamento, getRoleFromToken } from '../api';
import {DARK_THEME_ORCAMENTO , LIGHT_THEME_ORCAMENTO} from '../componets/temas'
// ─── CONTROLE DE ACESSO POR DEPARTAMENTO ──────────────────────────────────────

const ROLES_FULL_ACCESS = ["ADMIN",  "GESTOR", "CEO", "FINANCEIRO_FULL"];

/**
 * Mapeamento role (token) → nome exato do departamento nas planilhas.
 */
const ROLE_TO_DEPARTAMENTO = {
  "CONSELHO":     "CONSELHO",
  "RH":           "Recursos Humanos",
  "TI":           "DESENVOLVIMENTO TECNOLÓGICO",
  "ADMFIN":       "DIRETORIA ADMINISTRATIVO FINANCEIRO",
  "NEGOCIOS":     "DIRETORIA DE DESENVOLVIMENTO DE NEGÓCIOS",
  "EXECUTIVA":    "DIRETORIA EXECUTIVA",
  "QUALIDADE":    "DIRETORIA DE PROCESSOS E QUALIDADE",
  "RH":           "DIRETORIA DE PESSOAS E CULTURA",
  "JURIDICO":     "DIRETORIA JURÍDICA",
  "OPERACAO":     "OPERAÇÃO",
  "COMERCIAL":    "DIRETORIA COMERCIAL"
};

// ─── CONTEXT ──────────────────────────────────────────────────────────────────

const ThemeContext = createContext(LIGHT_THEME_ORCAMENTO);

const parseBRL = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number")  return v;

  const s = String(v).trim();

  if (s.includes(",")) {
    const n = parseFloat(s.replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? null : n;
  }

  if (s.includes(".")) {
    const afterDot = s.split(".").pop();
    if (afterDot.length === 3) {
      const n = parseFloat(s.replace(/\./g, ""));
      return isNaN(n) ? null : n;
    }
    return parseFloat(s);
  }

  return parseFloat(s);
};

const MESES_PT = {
  janeiro:1, fevereiro:2, março:3, marco:3, abril:4, maio:5, junho:6,
  julho:7, agosto:8, setembro:9, outubro:10, novembro:11, dezembro:12,
};

const toMonthKey = (d) => {
  if (!d) return null;
  const s = String(d).trim();

  const slashParts = s.split("/");
  if (slashParts.length === 3) return `${slashParts[1]}/${slashParts[2]}`;
  if (slashParts.length === 2) {
    const nomeMes = MESES_PT[slashParts[0].toLowerCase()];
    if (nomeMes) return `${String(nomeMes).padStart(2,"0")}/${slashParts[1]}`;
    return s;
  }

  const nomeMes = MESES_PT[s.toLowerCase()];
  if (nomeMes) return String(nomeMes).padStart(2,"0");

  return s;
};

const monthNumFromKey = (key) => key ? key.split("/")[0] : null;

const monthLabel = (key) => {
  if (!key) return key;
  const [mm, yyyy] = key.split("/");
  const months = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const label = months[parseInt(mm,10)-1] || mm;
  return yyyy ? `${label} ${yyyy}` : label;
};

const fmt = (v) =>
  v == null ? "-" :
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits:2, maximumFractionDigits:2 }).format(v);

const fmtShort = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1_000_000) return `${(v/1_000_000).toFixed(2)} M`;
  if (Math.abs(v) >= 1_000)     return `${(v/1_000).toFixed(0)} mil`;
  return fmt(v);
};

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────

function Select({ label, options, value, onChange, disabled = false }) {
  const C = useContext(ThemeContext);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4, minWidth:190 }}>
      <label style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:C.text, textTransform:"uppercase", display:"flex", alignItems:"center", gap:5 }}>
        {label}
        {disabled && (
          <span title="Filtro restrito ao seu departamento" style={{ fontSize:11, color:C.amber, lineHeight:1 }}>🔒</span>
        )}
      </label>
      <select
        value={value}
        onChange={(e) => !disabled && onChange(e.target.value)}
        disabled={disabled}
        style={{
          background: disabled ? C.inputDisabled : C.cardBg,
          color: disabled ? C.muted : C.text,
          border: `1px solid ${disabled ? C.amber + "55" : C.border}`,
          borderRadius:6, padding:"10px 65px 10px 65px", fontSize:13,
          fontFamily:"inherit", appearance:"none",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238B90A0' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
          backgroundRepeat:"no-repeat", backgroundPosition:"right 10px center",
          outline:"none", opacity: disabled ? 0.7 : 1,
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function MultiSelect({ label, options, value, onChange }) {
  const C = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [isOpen]);

  const toggleOption = (optValue) => {
    if (optValue === "all") {
      onChange(["all"]);
    } else {
      const newValue = value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value.filter((v) => v !== "all"), optValue];
      onChange(newValue.length === 0 ? ["all"] : newValue);
    }
  };

  return (
    <div
      style={{ display:"flex", flexDirection:"column", gap:4, minWidth:190, position:"relative" }}
      onClick={(e) => e.stopPropagation()}
    >
      <label style={{ fontSize:10, fontWeight:600, letterSpacing:"0.12em", color:C.text, textTransform:"uppercase" }}>{label}</label>

      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background:C.cardBg, color:C.text, border:`1px solid ${C.border}`,
          borderRadius:6, padding:"10px 65px", fontSize:13, cursor:"pointer",
          display:"flex", justifyContent:"space-between", alignItems:"center",
          minHeight: "36px"
        }}
      >
        <span style={{ whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:150 }}>
          {value.includes("all") ? "Todas as Categorias" : `${value.length} selecionadas`}
        </span>
        <span style={{ fontSize:10, color:C.muted }}>{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <div style={{
          position:"absolute", top:"100%", left:0, right:0, zIndex:1000,
          background:C.cardBg, border:`1px solid ${C.border}`, borderRadius:6,
          marginTop:4, padding:"8px 0", maxHeight:500, overflowY:"auto",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)"
        }}>
          {options.map(o => (
            <div
              key={o.value}
              onClick={() => toggleOption(o.value)}
              style={{
                padding:"8px 12px", display:"flex", alignItems:"center", gap:10,
                cursor:"pointer", transition:"background 0.2s"
              }}
              onMouseEnter={e => e.currentTarget.style.background = C.hover}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                readOnly
                style={{ accentColor: C.crimson, cursor:"pointer" }}
              />
              <span style={{ fontSize:13, color: value.includes(o.value) ? C.text : C.muted }}>
                {o.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, accent=false, large=false }) {
  const C = useContext(ThemeContext);
  return (
    <div style={{ background:C.cardBg, border:`1px solid ${accent?C.crimson:C.border}`, borderRadius:10, padding:large?"20px 28px":"16px 20px", display:"flex", flexDirection:"column", gap:6, boxShadow:accent?`0 0 24px ${C.crimson}33`:"none" }}>
      <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.14em", color:C.muted, textTransform:"uppercase" }}>{label}</span>
      <span style={{ fontSize:large?26:20, fontWeight:700, color:accent?C.crimson:C.text, fontVariantNumeric:"tabular-nums" }}>{value}</span>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  const C = useContext(ThemeContext);
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.tooltipBg, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px", boxShadow:"0 4px 16px rgba(0,0,0,0.12)" }}>
      <p style={{ margin:"0 0 6px 0", fontSize:11, color:C.muted }}>{label}</p>
      {payload.map(p => <p key={p.name} style={{ margin:"2px 0", fontSize:13, color:p.color, fontWeight:600 }}>{p.name}: {fmtShort(p.value)}</p>)}
    </div>
  );
}

function Spinner() {
  const C = useContext(ThemeContext);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:320, gap:16 }}>
      <div style={{ width:40, height:40, border:`3px solid ${C.border}`, borderTop:`3px solid ${C.crimson}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <span style={{ color:C.muted, fontSize:13 }}>Carregando dados…</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  const C = useContext(ThemeContext);
  return (
    <div style={{ background: C.deepBg, border:`1px solid ${C.danger}55`, borderRadius:10, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, marginBottom:24 }}>
      <span style={{ color:C.danger, fontSize:13 }}>⚠️ {message}</span>
      <button onClick={onRetry} style={{ background:C.crimson, color:"#fff", border:"none", borderRadius:6, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer" }}>Tentar novamente</button>
    </div>
  );
}

// ─── THEME TOGGLE BUTTON ──────────────────────────────────────────────────────

function ThemeToggle({ isLight, onToggle }) {
  const C = useContext(ThemeContext);
  return (
    <button
      onClick={onToggle}
      title={isLight ? "Mudar para tema escuro" : "Mudar para tema claro"}
      style={{
        display:"flex", alignItems:"center", gap:7,
        background: isLight ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)",
        border: `1px solid ${isLight ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
        borderRadius:8, padding:"7px 13px", cursor:"pointer",
        color:"#fff", fontSize:12, fontWeight:600,
        transition:"all 0.2s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.22)"}
      onMouseLeave={e => e.currentTarget.style.background = isLight ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)"}
    >
      {isLight ? <Sun size={14} /> : <Moon size={14} />}
      {isLight ? "Tema Escuro" : "Tema Claro"}
    </button>
  );
}

// ─── DEPARTMENT DRAWER ────────────────────────────────────────────────────────

function DepartmentDrawer({ dep, lancamentos, onClose }) {
  const C = useContext(ThemeContext);
  const [expandedCats, setExpandedCats] = useState({});

  if (!dep) return null;

  const parseDate = (d) => {
    if (!d) return 0;
    const [dd, mm, yyyy] = String(d).split("/");
    return new Date(`${yyyy}-${mm}-${dd}`).getTime();
  };

  const rows = lancamentos
    .filter(r => (r.Departamento || "-") === dep)
    .sort((a, b) => parseDate(b["Data (completa)"]) - parseDate(a["Data (completa)"]));

  const catMap = {};
  rows.forEach(r => {
    const cat = r.Categoria || "Sem Categoria";
    if (!catMap[cat]) catMap[cat] = [];
    catMap[cat].push(r);
  });

  const categorias = Object.entries(catMap)
    .map(([cat, items]) => {
      const total = items.reduce((s, r) => {
        const v = typeof r.Valor === "number" ? r.Valor : parseBRL(r.Valor);
        return s + (v || 0);
      }, 0);
      return { cat, items, total };
    })
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

  const totalDep = categorias.reduce((s, c) => s + c.total, 0);

  const toggleCat = (cat) =>
    setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const expandAll   = () => setExpandedCats(Object.fromEntries(categorias.map(c => [c.cat, true])));
  const collapseAll = () => setExpandedCats({});
  const anyOpen     = categorias.some(c => expandedCats[c.cat]);

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"#00000088", zIndex:100, backdropFilter:"blur(2px)" }} />

      {/* Painel lateral */}
      <div style={{
        position:"fixed", top:0, right:0, bottom:0,
        width:"min(850px, 96vw)",
        background:C.drawerBg,
        border:`1px solid ${C.border}`,
        borderRight:"none",
        zIndex:101,
        display:"flex",
        flexDirection:"column",
        boxShadow:"-8px 0 40px rgba(0,0,0,0.25)",
      }}>

        {/* Header */}
        <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, background:C.panelBg }}>
          <div>
            <p style={{ margin:0, fontSize:10, fontWeight:700, letterSpacing:"0.14em", color:C.muted, textTransform:"uppercase" }}>
              Detalhamento · Realizado
            </p>
            <h2 style={{ margin:"4px 0 0", fontSize:16, fontWeight:700, color:C.text }}>{dep}</h2>
            <p style={{ margin:"6px 0 0", fontSize:12, color:C.muted }}>
              {categorias.length} categori{categorias.length !== 1 ? "as" : "a"}
              <span style={{ margin:"0 8px", color:C.border }}>·</span>
              {rows.length} lançamento{rows.length !== 1 ? "s" : ""}
              <span style={{ marginLeft:12, fontWeight:700, color:totalDep < 0 ? C.danger : C.success }}>
                Total: R$ {fmt(totalDep*-1)}
              </span>
            </p>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
            <button
              onClick={anyOpen ? collapseAll : expandAll}
              title={anyOpen ? "Recolher tudo" : "Expandir tudo"}
              style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, fontSize:11, fontWeight:600, padding:"5px 10px", cursor:"pointer", whiteSpace:"nowrap" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.crimson; e.currentTarget.style.color = C.crimson; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;  e.currentTarget.style.color = C.muted; }}
            >
              {anyOpen ? "− Recolher" : "+ Expandir"}
            </button>
            <button
              onClick={onClose}
              style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:18, lineHeight:1, width:34, height:34, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}
            >×</button>
          </div>
        </div>

        {/* Lista de categorias */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {categorias.length === 0 ? (
            <div style={{ padding:48, textAlign:"center", color:C.muted, fontSize:13 }}>
              Nenhum lançamento encontrado para os filtros ativos.
            </div>
          ) : (
            categorias.map(({ cat, items, total }, ci) => {
              const isOpen = !!expandedCats[cat];
              const corTotal = total < 0 ? C.danger : C.success;

              return (
                <div key={cat} style={{ borderBottom:`1px solid ${C.border}` }}>

                  {/* Linha da categoria */}
                  <div
                    onClick={() => toggleCat(cat)}
                    style={{
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"13px 20px",
                      cursor:"pointer",
                      background: isOpen ? C.subBg : ci % 2 === 0 ? "transparent" : C.rowAlt88,
                      transition:"background 0.12s",
                      userSelect:"none",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = `${C.crimson}12`}
                    onMouseLeave={e => e.currentTarget.style.background = isOpen ? C.subBg : ci % 2 === 0 ? "transparent" : C.rowAlt88}
                  >
                    <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
                      <span style={{
                        fontSize:12, color: isOpen ? C.crimson : C.muted,
                        transition:"transform 0.2s, color 0.15s",
                        display:"inline-block",
                        transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                        flexShrink:0,
                      }}>▶</span>
                      <span style={{ fontSize:13, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={cat}>
                        {cat}
                      </span>
                    </div>

                    <div style={{ display:"flex", alignItems:"center", gap:12, flexShrink:0, marginLeft:12 }}>
                      <span style={{ fontSize:11, color:C.muted, background:C.hover, borderRadius:12, padding:"2px 8px" }}>
                        {items.length} lanç.
                      </span>
                      <span style={{ fontSize:13, fontWeight:700, color:corTotal, fontVariantNumeric:"tabular-nums", minWidth:90, textAlign:"right" }}>
                        R$ {fmt(total*-1)}
                      </span>
                    </div>
                  </div>

                  {/* Sub-tabela de lançamentos */}
                  {isOpen && (
                    <div style={{ background:C.deepBg, borderTop:`1px solid ${C.border}22` }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                        <thead>
                          <tr style={{ background:C.deepBg2 }}>
                            <th style={{ padding:"8px 16px 8px 36px", textAlign:"left",   fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", width:"38%" }}>Cliente / Fornecedor</th>
                            <th style={{ padding:"8px 16px",           textAlign:"right",  fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", width:"22%" }}>Valor</th>
                            <th style={{ padding:"8px 16px",           textAlign:"center", fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", width:"20%" }}>Data</th>
                            <th style={{ padding:"8px 16px",           textAlign:"center", fontSize:9, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", width:"20%" }}>Observação da Conta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((r, i) => {
                            const val = typeof r.Valor === "number" ? r.Valor : parseBRL(r.Valor);
                            const cf  = r["Cliente ou Fornecedor"] || "-";
                            const nome = cf.replace(/^\d[\d.\/\-]+\s+/, "");
                            const doc  = cf !== nome ? (cf.match(/^[\d.\/\-]+/)?.[0] || "") : "";

                            return (
                              <tr
                                key={i}
                                style={{ borderTop:`1px solid ${C.border}18`, background: i % 2 === 0 ? "transparent" : C.rowAlt88, transition:"background 0.1s" }}
                                onMouseEnter={e => e.currentTarget.style.background = `${C.crimson}0D`}
                                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : C.rowAlt88}
                              >
                                <td style={{ padding:"8px 16px 8px 36px", color:C.text, maxWidth:0 }}>
                                  <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:500 }} title={cf}>
                                    {nome || cf}
                                  </div>
                                  {doc && (
                                    <div style={{ fontSize:10, color:C.muted, marginTop:1 }}>{doc}</div>
                                  )}
                                </td>
                                <td style={{ padding:"8px 16px", textAlign:"right", fontWeight:700, fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap", color: val == null ? C.muted : val < 0 ? C.danger : C.success }}>
                                  {val != null ? `R$ ${fmt(val*-1)}` : "-"}
                                </td>
                                <td style={{ padding:"8px 16px", textAlign:"center", color:C.muted, whiteSpace:"nowrap" }}>
                                  {r["Data (completa)"] || "-"}
                                </td>
                                <td style={{ padding:"5px 11px", textAlign:"center", color:C.muted, whiteSpace:"nowrap" }}>
                                  {r["Obs da Conta"] || "-"}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Subtotal da categoria */}
                          <tr style={{ borderTop:`1px solid ${C.border}44`, background:C.panelBg }}>
                            <td style={{ padding:"8px 16px 8px 36px", fontSize:11, fontWeight:700, color:C.muted }}>
                              Subtotal
                            </td>
                            <td style={{ padding:"8px 16px", textAlign:"right", fontSize:12, fontWeight:800, fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap", color:corTotal }}>
                              R$ {fmt(total*-1)}
                            </td>
                            <td /><td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 24px", borderTop:`1px solid ${C.border}`, background:C.panelBg, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:11, color:C.muted }}>
            {categorias.length} categori{categorias.length !== 1 ? "as" : "a"} · {rows.length} lançamento{rows.length !== 1 ? "s" : ""}
          </span>
          <span style={{ fontSize:13, fontWeight:700, color:totalDep < 0 ? C.danger : C.success }}>
            Total: R$ {fmt(totalDep)}
          </span>
        </div>

      </div>
    </>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function FinanceiroDashboard() {
  const [isLight, setisLight] = useState(true);
  const C = isLight ? LIGHT_THEME_ORCAMENTO : DARK_THEME_ORCAMENTO;

  const [lancamentos, setLancamentos] = useState([]);
  const [despesas, setDespesas]       = useState([]);
  const [orcamento, setOrcamento]     = useState([]);
  const [status, setStatus]           = useState({ loading:false, error:null });
  const [open, setOpen]               = useState(false);
  const [drawerDep, setDrawerDep]     = useState(null);

  const roleAtual      = getRoleFromToken();
  const depBloqueado   = roleAtual && !ROLES_FULL_ACCESS.includes(roleAtual?.toUpperCase?.());
  const depDoToken     = ROLE_TO_DEPARTAMENTO[roleAtual?.toUpperCase?.()] 
                      ?? ROLE_TO_DEPARTAMENTO[roleAtual] 
                      ?? roleAtual 
                      ?? "all";
  const depInicialDoToken = depBloqueado ? depDoToken : "all";

  const [filtCatLanc,         setFiltCatLanc]         = useState(["all"]);
  const [filtCatSuperiorLanc, setFiltCatSuperiorLanc] = useState(["all"]);
  const [filtMes,             setFiltMes]             = useState("all");
  const [filtDep,             setFiltDep]             = useState(depInicialDoToken);

  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate("/"); };

  const fetchDados = async () => {
    setStatus({ loading: true, error: null });
    try {
      const [jL, jD, jO] = await Promise.all([
        getDadosLancamentosDash(),
        getDadosDespesas(),
        getDadosOrcamento(),
      ]);
      console.log('jO :>> ', jO);
      console.log('jL :>> ', jL);

      if (!jL.success) throw new Error(`Lançamentos: ${jL.error}`);
      if (!jD.success) throw new Error(`Despesas: ${jD.error}`);
      if (!jO.success) throw new Error(`Orçamento: ${jO.error}`);

      setLancamentos(jL.data || []);
      setDespesas(jD.data    || []);
      setOrcamento(jO.data   || []);
      setStatus({ loading: false, error: null });
    } catch (err) {
      setStatus({ loading: false, error: err.message });
    }
  };

  useEffect(() => { fetchDados(); }, []);

  const getDataAtualizacao = () => {
    const agora = new Date();
    const horaAtual = agora.getHours();
    if (horaAtual < 12) agora.setDate(agora.getDate() - 1);
    const dataFormatada = agora.toLocaleDateString('pt-BR');
    return `${dataFormatada} - 12:00`;
  };

  const catLancOpcoes = useMemo(() => {
    const fromLanc = lancamentos.map(r => r.Categoria).filter(Boolean);
    const fromOrc  = orcamento.map(r => r.CONTA).filter(Boolean);
    const u = [...new Set([...fromLanc, ...fromOrc])].sort();
    return [{ value:"all", label:"Categorias" }, ...u.map(c => ({ value:c, label:c }))];
  }, [lancamentos, orcamento]);

  const catSuperiorLancOpcoes = useMemo(() => {
    const fromLanc = lancamentos.map(r => r.Grupo).filter(Boolean);
    const fromOrc  = orcamento.map(r => r.Grupo).filter(Boolean);
    const u = [...new Set([...fromLanc, ...fromOrc])].sort();
    return [{ value:"all", label:"Categorias Superiores" }, ...u.map(c => ({ value:c, label:c }))];
  }, [lancamentos, orcamento]);

  const depOpcoes = useMemo(() => {
    const fromLanc = lancamentos.map(r => r.Departamento).filter(Boolean);
    const fromOrc  = orcamento.map(r => r.Departamento).filter(Boolean);
    const u = [...new Set([...fromLanc, ...fromOrc])].sort();
    return [{ value:"all", label:"Todos os Departamentos" }, ...u.map(d => ({ value:d, label:d }))];
  }, [lancamentos, orcamento]);

  const mesOpcoes = useMemo(() => {
    const keysLanc = lancamentos.map(r => toMonthKey(r["Data (completa)"])).filter(Boolean);
    const keysOrc  = orcamento.map(r => toMonthKey(r.Periodo)).filter(Boolean);
    const anosPorMes = {};
    keysLanc.forEach(k => { const [mm, yyyy] = k.split("/"); if (mm && yyyy) anosPorMes[mm] = yyyy; });
    keysOrc.forEach(k  => { const [mm, yyyy] = k.split("/"); if (mm && yyyy) anosPorMes[mm] = yyyy; });

    const keysDesp = despesas.map(r => {
      const base = toMonthKey(r.Periodo);
      if (!base) return null;
      if (base.includes("/")) return base;
      const ano = anosPorMes[base];
      return ano ? `${base}/${ano}` : null;
    }).filter(Boolean);

    const allKeys = [...new Set([...keysLanc, ...keysOrc, ...keysDesp])]
      .filter(k => k.includes("/"))
      .sort((a, b) => {
        const [ma, ya] = a.split("/").map(Number);
        const [mb, yb] = b.split("/").map(Number);
        return ya !== yb ? ya - yb : ma - mb;
      });

    return [
      { value:"all", label:"Todos os Meses" },
      ...allKeys.map(k => ({ value:k, label:monthLabel(k) })),
    ];
  }, [lancamentos, orcamento, despesas]);

  const lancFiltrados = useMemo(() =>
    lancamentos.filter(r => {
      const matchesCatSuperior = filtCatSuperiorLanc.includes("all") || filtCatSuperiorLanc.includes(r.Grupo);
      const matchesCat         = filtCatLanc.includes("all") || filtCatLanc.includes(r.Categoria);
      if (!matchesCat) return false;
      if (!matchesCatSuperior) return false;
      if (filtDep !== "all" && (r.Departamento || "-") !== filtDep) return false;
      if (filtMes !== "all" && toMonthKey(r["Data (completa)"]) !== filtMes) return false;
      return true;
    }),
    [lancamentos, filtCatLanc, filtCatSuperiorLanc, filtDep, filtMes]
  );

  const orcFiltrado = useMemo(() =>
    orcamento.filter(r => {
      const matchesCatSuperior = filtCatSuperiorLanc.includes("all") || filtCatSuperiorLanc.includes(r.Grupo);
      const matchesCat         = filtCatLanc.includes("all") || filtCatLanc.includes(r.CONTA);
      if (!matchesCat) return false;
      if (!matchesCatSuperior) return false;
      if (filtDep !== "all" && (r.Departamento || "-") !== filtDep) return false;
      return filtMes === "all" || toMonthKey(r.Periodo) === filtMes;
    }),
    [orcamento, filtCatLanc, filtCatSuperiorLanc, filtDep, filtMes]
  );

  const despFiltradas = useMemo(() =>
    despesas.filter(r => {
      if (filtMes === "all") return true;
      const keyDesp = toMonthKey(r.Periodo);
      const mmFiltro = monthNumFromKey(filtMes);
      const mmDesp   = monthNumFromKey(keyDesp);
      return keyDesp && keyDesp.includes("/") ? keyDesp === filtMes : mmDesp === mmFiltro;
    }),
    [despesas, filtMes]
  );

  const merged = useMemo(() => {
    const realizadoMap = {};
    lancFiltrados.forEach(r => {
      const dep = r.Departamento || "-";
      const val = parseBRL(r.Valor);
      if (val != null) realizadoMap[dep] = (realizadoMap[dep] || 0) + val;
    });

    const orcMap = {};
    orcFiltrado.forEach(r => {
      const dep = r.Departamento || "-";
      const raw = r["Orçamento"] ?? r["Orcamento"] ?? r["orcamento"] ?? r["orçamento"];
      const val = parseBRL(raw);
      if (val != null) orcMap[dep] = (orcMap[dep] || 0) + val;
    });

    const aprovadoMap = {};
    despFiltradas.forEach(r => {
      const dep = r.Departamento || "-";
      const val = parseBRL(r.Valor);
      if (val != null) aprovadoMap[dep] = (aprovadoMap[dep] || 0) + val;
    });

    const deps = [
      ...new Set([
        ...Object.keys(realizadoMap),
        ...Object.keys(orcMap),
        ...Object.keys(aprovadoMap),
      ]),
    ].sort();

    return deps.map(dep => ({
      Departamento: dep,
      Realizado:    realizadoMap[dep] ?? null,
      Orçamento:    orcMap[dep]       ?? null,
      Aprovado:     aprovadoMap[dep]  ?? null,
    }));
  }, [lancFiltrados, orcFiltrado, despFiltradas]);

  const kpis = useMemo(() => {
    const totalOrc      = merged.reduce((s,r) => s + (r.Orçamento  || 0), 0);
    const totalReal     = merged.reduce((s,r) => s + (r.Realizado  || 0), 0);
    const totalAprovado = merged.reduce((s,r) => s + (r.Aprovado   || 0), 0);
    return { totalOrc, totalReal, totalAprovado, deltaReal: totalOrc + totalReal, deltaRealAprov: (totalOrc + totalReal) - totalAprovado };
  }, [merged]);

  const chartData = useMemo(() =>
    merged
      .filter(r => r.Departamento !== "-" && (r.Orçamento || r.Realizado || r.Aprovado))
      .map(r => ({
        name: r.Departamento.replace(/DIRETORIA\s+/i,"DIR. ").substring(0,22),
        Orçamento: r.Orçamento,
        Realizado: r.Realizado != null ? Math.abs(r.Realizado) : null,
        Aprovado:  r.Aprovado,
      })),
    [merged]
  );

  const isInitialLoad = status.loading && lancamentos.length === 0;

  return (
    <ThemeContext.Provider value={C}>
      <div style={{ background:C.darkBg, minHeight:"100vh", fontFamily:"'DM Sans','Segoe UI',sans-serif", color:C.text, transition:"background 0.25s, color 0.25s" }}>

        {/* Drawer de detalhamento por departamento */}
        {drawerDep && (
          <DepartmentDrawer
            dep={drawerDep}
            lancamentos={lancFiltrados}
            onClose={() => setDrawerDep(null)}
          />
        )}

        {/* Header */}
        <header style={{ background:C.headerGradient }} className="text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div>
              <span className="text-2xl font-bold">YOUX</span>
              <span className="text-xl font-light">GROUP</span>
              <h1 style={{ color:C.headerText }} className="text-[10px] uppercase tracking-wider">
                Atualizado em {getDataAtualizacao()}
              </h1>
            </div>
          </div>

          <MultiSelect
            label="Categorias"
            options={catLancOpcoes}
            value={filtCatLanc}
            onChange={setFiltCatLanc}
          />
          <MultiSelect
            label="Categorias Superiores"
            options={catSuperiorLancOpcoes}
            value={filtCatSuperiorLanc}
            onChange={setFiltCatSuperiorLanc}
          />
          <Select label="Departamento" options={depOpcoes} value={filtDep} onChange={setFiltDep} disabled={depBloqueado} />
          <Select label="Mês"          options={mesOpcoes} value={filtMes} onChange={setFiltMes} />

          {/* Toggle de tema */}
          <ThemeToggle isLight={isLight} onToggle={() => setisLight(d => !d)} />

          <button
            onClick={() => navigate('/inicio')}
            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
          >
            Menu Principal
          </button>
        </header>

        <div style={{ padding:"32px 40px", maxWidth:1400, margin:"0 auto" }}>
          {status.error && <ErrorBanner message={status.error} onRetry={fetchDados} />}

          {isInitialLoad ? <Spinner /> : (
            <>
              <div style={{ display:"flex", justifyContent:"center", marginBottom:32 }}>
                {/* <KpiCard label="Aprovado (Despesas)" value={`R$ ${fmt(kpis.totalAprovado)}`} accent large /> */}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))", gap:14, marginBottom:40 }}>
                <KpiCard label="Orçamento"       value={`R$ ${fmt(kpis.totalOrc)}`}      />
                <KpiCard label="Realizado"        value={`R$ ${fmt(kpis.totalReal*-1)}`}  />
                <KpiCard label="Delta Realizado"  value={`R$ ${fmt(kpis.deltaReal)}`}     />
              </div>

              {/* Gráfico */}
              <div style={{ background:C.cardBg, border:`1px solid ${C.border}`, borderRadius:12, padding:"24px 20px", marginBottom:36 }}>
                <h3 style={{ margin:"0 0 20px 0", fontSize:11, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase" }}>
                  Orçamento · Realizado · Aprovado — por Departamento
                </h3>
                {chartData.length === 0
                  ? <div style={{ textAlign:"center", padding:60, color:C.muted, fontSize:13 }}>Nenhum dado para os filtros selecionados.</div>
                  : (
                    <ResponsiveContainer width="100%" height={540}>
                      <BarChart data={chartData} margin={{ top:4, right:8, left:0, bottom:80 }} barGap={2} barCategoryGap="25%">
                        <CartesianGrid vertical={false} stroke={C.border} />
                        <XAxis dataKey="name" tick={{ fill:C.muted, fontSize:10 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                        <YAxis tickFormatter={fmtShort} tick={{ fill:C.muted, fontSize:10 }} axisLine={false} tickLine={false} width={72} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill:`${C.danger}08` }} />
                        <Legend wrapperStyle={{ marginBottom:500, fontSize:12, color:C.muted }} iconType="square" iconSize={10} />
                        <Bar dataKey="Orçamento" fill={C.crimson} radius={[3,3,0,0]} maxBarSize={55} />
                        <Bar dataKey="Realizado" fill={C.amber}   radius={[3,3,0,0]} maxBarSize={36} />
                      </BarChart>
                    </ResponsiveContainer>
                  )
                }
              </div>

              {/* Tabela */}
              <div style={{ background:C.cardBg, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ borderBottom:`1px solid ${C.border}`, background:C.panelBg }}>
                      {["Departamento","Orçamento","Realizado","Delta"].map(col => (
                        <th key={col} style={{ padding:"13px 20px", textAlign:col==="Departamento"?"left":"right", fontSize:10, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase" }}>{col}</th>
                      ))}
                      <th style={{ padding:"13px 20px", textAlign:"center", fontSize:10, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", width:90 }}>% Exec.</th>
                      <th style={{ padding:"13px 16px", textAlign:"center", fontSize:10, fontWeight:700, letterSpacing:"0.12em", color:C.muted, textTransform:"uppercase", width:60 }}>Detalhe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {merged.map((row,i) => (
                      <tr
                        key={row.Departamento}
                        style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?"transparent":C.rowAlt88, transition:"background 0.12s" }}
                        onMouseEnter={e => e.currentTarget.style.background=`${C.crimson}18`}
                        onMouseLeave={e => e.currentTarget.style.background=i%2===0?"transparent":C.rowAlt88}
                      >
                        <td style={{ padding:"11px 20px", color:C.text, fontWeight:500 }}>{row.Departamento}</td>
                        <td style={{ padding:"11px 20px", textAlign:"right", color:C.text, fontVariantNumeric:"tabular-nums" }}>{row.Orçamento!=null?fmt(row.Orçamento):"-"}</td>
                        <td style={{ padding:"11px 20px", textAlign:"right", fontVariantNumeric:"tabular-nums", fontWeight:600, color:row.Realizado==null?C.muted:row.Realizado<0?C.danger:C.success }}>{row.Realizado!=null?fmt(row.Realizado*-1):"-"}</td>
                        <td style={{ padding:"11px 20px", textAlign:"right", color:C.text, fontVariantNumeric:"tabular-nums" }}>
                          {(() => {
                            const delta = (row.Orçamento ?? 0) + (row.Realizado ?? 0);
                            if (row.Orçamento == null && row.Realizado == null) return "-";
                            return <span style={{ fontWeight:600, color: delta < 0 ? C.danger : C.success }}>{fmt(delta)}</span>;
                          })()}
                        </td>
                        <td style={{ padding:"11px 20px", textAlign:"center" }}>
                          {(() => {
                            const orc  = row.Orçamento;
                            const real = row.Realizado;
                            if (orc == null || orc === 0 || real == null) return <span style={{ color:C.muted }}>—</span>;
                            const pct = (Math.abs(real) / Math.abs(orc)) * 100;
                            const cor = pct >= 100 ? "#FF2D55" : pct >= 80 ? C.amber : pct >= 60 ? "#F0C040" : C.success;
                            const bg  = pct >= 100 ? "#FF2D5515" : pct >= 80 ? `${C.amber}18` : pct >= 60 ? "#F0C04015" : `${C.success}12`;
                            return (
                              <span style={{
                                display:"inline-block", minWidth:60, padding:"3px 8px",
                                borderRadius:20, fontSize:12, fontWeight:700,
                                color:cor, background:bg, fontVariantNumeric:"tabular-nums",
                                border:`1px solid ${cor}44`,
                              }}>
                                {pct.toFixed(1)}%
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding:"11px 16px", textAlign:"center" }}>
                          <button
                            onClick={() => setDrawerDep(row.Departamento)}
                            title={`Ver lançamentos de ${row.Departamento}`}
                            style={{
                              background: "transparent",
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              color: C.muted,
                              cursor: "pointer",
                              fontSize: 15,
                              width: 30, height: 30,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "border-color 0.15s, color 0.15s, background 0.15s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = C.crimson; e.currentTarget.style.color = C.crimson; e.currentTarget.style.background = `${C.crimson}15`; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;  e.currentTarget.style.color = C.muted;   e.currentTarget.style.background = "transparent"; }}
                          >›</button>
                        </td>
                      </tr>
                    ))}
                    {/* Linha de total */}
                    <tr style={{ borderTop:`2px solid ${C.border}`, background:C.panelBg }}>
                      <td style={{ padding:"13px 20px", fontWeight:800, color:C.text }}>Total geral</td>
                      <td style={{ padding:"13px 20px", textAlign:"right", fontWeight:800, fontVariantNumeric:"tabular-nums", color:C.text }}>{fmt(kpis.totalOrc)}</td>
                      <td style={{ padding:"13px 20px", textAlign:"right", fontWeight:800, fontVariantNumeric:"tabular-nums", color:kpis.totalReal<0?C.danger:C.success }}>{fmt(kpis.totalReal*-1)}</td>
                      <td style={{ padding:"13px 20px", textAlign:"right", fontWeight:800, fontVariantNumeric:"tabular-nums", color:kpis.deltaReal < 0 ? C.danger : C.success }}>{fmt(kpis.deltaReal)}</td>
                      <td style={{ padding:"13px 20px", textAlign:"center", fontWeight:800 }}>
                        {(() => {
                          const pct = kpis.totalOrc !== 0 ? (Math.abs(kpis.totalReal) / Math.abs(kpis.totalOrc)) * 100 : null;
                          if (pct == null) return <span style={{ color:C.muted }}>—</span>;
                          const cor = pct >= 100 ? "#FF2D55" : pct >= 80 ? C.amber : pct >= 60 ? "#F0C040" : C.success;
                          return <span style={{ color:cor, fontVariantNumeric:"tabular-nums" }}>{pct.toFixed(1)}%</span>;
                        })()}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>

              <p style={{ marginTop:14, fontSize:11, color:C.muted, textAlign:"right" }}>
                {lancamentos.length} lançamentos · {orcamento.length} orçamentos · {despesas.length} despesas
              </p>
            </>
          )}
        </div>
      </div>
    </ThemeContext.Provider>
  );
}